import { default as React, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { Breadcrumbs } from "~/components/Breadcrumps";
import { Button } from "~/components/Button";
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import { useConfirm } from "~/components/Dialogs";
import { onSubmitError } from "~/components/Form";
import { useModals } from "~/components/Modal";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort, TableSortDirection } from "~/components/Table";
import { Title } from "~/components/Title";
import { useAbility } from "~/lib/ability";
import { useDataSource } from "~/lib/dataSource";
import { Intent } from "~/lib/intent";
import { sortByNumber, sortByString } from "~/lib/sort";
import { usersLinks } from "~/modules/Users";
import { UsersAbility } from "~/modules/Users/ability";
import { usersAdapter } from "~/modules/Users/adapters";
import { deleteUser } from "~/modules/Users/api";
import { UserForm } from "~/modules/Users/forms/UserForm";
import { defaultUser, getUserRoleLabel, userRoles } from "~/modules/Users/models/User";
import style from "./index.module.scss";
import { Input } from "~/components/Controls/Input";
import { MultiSelectInput } from "~/components/Controls/Select";

export const UsersView = () => {
  const ability = useAbility(UsersAbility);
  let { state, fetchRecords, createRecord, updateRecord } = useDataSource(usersAdapter);
  const location = useLocation();
  const navigate = useNavigate();
  const modals = useModals();
  const confirm = useConfirm();

  const queryParams = new URLSearchParams(location.search);

  useEffect(() => fetchRecords(), []);

  const filterId = queryParams.get("id");
  const filterName = queryParams.get("name");
  const filterCompany = queryParams.get("company");
  const filterEmail = queryParams.get("email");
  const filterRole = queryParams.get("role") ? JSON.parse(queryParams.get("role")!) : null;

  const filteredRecords = useMemo(() => {
    return state.records.filter((record) => {
      return (
        (filterId ? record.id.toString().includes(filterId) : true) &&
        (filterName ? record.name.toLowerCase().includes(filterName.toLowerCase()) : true) &&
        (filterCompany ? record.company?.toLowerCase().includes(filterCompany.toLowerCase()) : true) &&
        (filterEmail ? record.email?.toLowerCase().includes(filterEmail.toLowerCase()) : true) &&
        (filterRole ? filterRole.includes(record.role) : true)
      );
    });
  }, [state.records, filterId, filterName, filterCompany, filterEmail, filterRole]);

  const createUser = modals.create(UserForm, {
    onSubmit: (newUser, formikActions, close) => {
      createRecord(newUser).then(close).catch(onSubmitError(formikActions));
    },
  });

  const editUser = modals.create(UserForm, {
    onSubmit: (updatedUser, formikActions, close) => {
      updateRecord(updatedUser).then(close).catch(onSubmitError(formikActions));
    },
  });

  const setQueryParams = (params: Record<string, string | null>) => {
    const newQueryParams = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === "") {
        newQueryParams.delete(key);
        continue;
      }
      newQueryParams.set(key, value);
    }
    navigate({ search: newQueryParams.toString() }, { replace: true });
  };

  return (
    <div>
      <div className="page-header-sticky">
        <div className="page-header-breadcrumbs">
          <Breadcrumbs />
        </div>
        <Title
          className={`${style.usersList} page-header-title`}
          toolbar={
          <>
            {ability.canCreate() && (
              <Button
                intent={Intent.PRIMARY}
                responsive
                disabled={state.isPending}
                onClick={() => createUser.show({ user: defaultUser })}
                leftIcon={<i className="fa fa-plus" />}
              >
                Create
              </Button>
            )}
            <Button
              responsive
              intent={Intent.SUCCESS}
              disabled={state.isPending}
              onClick={() => fetchRecords()}
              leftIcon={<i className="fa fa-refresh" />}
            >
              Reload
            </Button>
          </>
        }
        >
          Users
        </Title>
      </div>
      <div className="layout-content-box">
        <div className={style.filters}>
          <Input
            value={filterId ?? ""}
            onChange={(value) => {
              setQueryParams({ id: value });
            }}
            placeholder="User ID"
            rightIcon={<i className="fa fa-search input-search-icon" />}
          />
          <Input
            value={filterName ?? ""}
            onChange={(value) => {
              setQueryParams({ name: value });
            }}
            placeholder="User Name"
            rightIcon={<i className="fa fa-search input-search-icon" />}
          />
          <Input
            value={filterCompany ?? ""}
            onChange={(value) => {
              setQueryParams({ company: value });
            }}
            placeholder="Company"
            rightIcon={<i className="fa fa-search input-search-icon" />}
          />
          <Input
            value={filterEmail ?? ""}
            onChange={(value) => {
              setQueryParams({ email: value });
            }}
            placeholder="Email"
            rightIcon={<i className="fa fa-search input-search-icon" />}
          />
          <MultiSelectInput
            items={userRoles}
            value={filterRole}
            onChange={(roles) => {
              if (roles.length === 0) {
                setQueryParams({ role: null });
                return;
              }
              setQueryParams({ role: JSON.stringify(roles) });
            }}
            placeholder="Role"
            className={style.roleFilter}
          />
          <div className={style.spacer} />
          <Button
            onClick={() => {
              setQueryParams({
                id: null,
                name: null,
                company: null,
                email: null,
                role: null,
              });
            }}
          >
            Reset
          </Button>
        </div>
      <TableSort
        records={filteredRecords}
        defaultSort={{
          sort: sortByNumber,
          field: "id",
          direction: TableSortDirection.DESC,
        }}
        header
        withPagination
        columns={[
          {
            header: "ID",
            small: true,
            field: "id",
            sort: sortByNumber,
          },
          {
            header: "Name",
            field: "name",
            sort: sortByString,
            cell: (user) => <NavLink to={usersLinks.user(user.id)}>{user.name}</NavLink>,
          },
          {
            header: "Company",
            field: "company",
            responsive: true,
            sort: sortByString,
          },
          {
            header: "Email",
            field: "email",
            responsive: true,
            sort: sortByString,
          },
          {
            header: "Role",
            field: "role",
            cell: (user) => getUserRoleLabel(user.role),
            responsive: true,
            sort: sortByString,
          },
          {
            small: true,
            cell: (user) => {
              const editButton = ability.canEdit() && (
                <Button
                  small
                  variant="menu"
                  leftIcon={<i className="fa fa-edit" />}
                  onClick={() => editUser.show({ user })}
                >
                  Edit
                </Button>
              );
              const deleteButton = ability.canDelete() && (
                <Button
                  small
                  variant="menu"
                  intent={Intent.DANGER}
                  leftIcon={<i className="fa fa-trash" />}
                  onClick={() =>
                    confirm("Delete user?", {
                      onOk: () => deleteUser(user.id).then(fetchRecords),
                      icon: <LucideIcon icon={Trash2} size={20} />,
                      intent: Intent.DANGER,
                    })
                  }
                >
                  Delete
                </Button>
              );
              return (
                <TableRowActions>
                  {editButton}
                  {deleteButton}
                </TableRowActions>
              );
            },
          },
        ]}
        placeholder={state.isPending && <Progress />}
      />
      </div>
    </div>
  );
};
